// POKERCITY DEALER CERTIFICATION — QUESTION BANK
// Track: Professional | Category: Professional Conduct
// 20 questions | Difficulty: E=Easy, M=Medium, H=Hard

export const PROFESSIONAL_CONDUCT_QUESTIONS = [

  // ── EASY (4 questions) ──────────────────────────────────────────────────

  {
    id: 'pc_001', track: 'professional', category: 'professional_conduct', difficulty: 'easy',
    question: 'A player tips the dealer after winning a large pot. What do you do?',
    options: [
      'A) Refuse the tip — dealers are not permitted to accept tips',
      'B) Accept the tip graciously and briefly acknowledge it without making a show',
      'C) Share the tip with all players at the table',
      'D) Announce the tip amount to the table'
    ],
    correct: 'B',
    explanation: 'Tipping is customary and appreciated. The dealer accepts the tip graciously with a brief "thank you" and moves on. Making a show of tips, announcing amounts, or soliciting tips is unprofessional.'
  },

  {
    id: 'pc_002', track: 'professional', category: 'professional_conduct', difficulty: 'easy',
    question: 'A player asks the dealer who they think had the best hand in a previous situation. What do you say?',
    options: [
      'A) Share their opinion — players value the dealer\'s perspective',
      'B) Decline to offer an opinion on strategy or hand analysis',
      'C) Say "I can\'t discuss strategy but the floor might help"',
      'D) Give a brief answer only if the hand is already over'
    ],
    correct: 'B',
    explanation: 'Dealers do not offer opinions on strategy, hand analysis, or how players should or should have played. This protects the dealer\'s neutrality and prevents accusations of favoritism or collusion.'
  },

  {
    id: 'pc_003', track: 'professional', category: 'professional_conduct', difficulty: 'easy',
    question: 'What is the standard regarding a dealer\'s use of their personal phone while dealing?',
    options: [
      'A) Phones are permitted between hands only',
      'B) Phones are not permitted at the table while dealing',
      'C) Phones may be used silently at any time',
      'D) Phone use is at the dealer\'s discretion'
    ],
    correct: 'B',
    explanation: 'Dealers do not use personal phones while dealing. Phone use distracts from the game, creates appearance of impropriety (collusion, messaging), and is unprofessional. Phones are stored away during a dealing shift.'
  },

  {
    id: 'pc_004', track: 'professional', category: 'professional_conduct', difficulty: 'easy',
    question: 'A player is telling an offensive joke at the table. Other players look uncomfortable. What do you do?',
    options: [
      'A) Laugh along to maintain a friendly atmosphere',
      'B) Politely ask the player to keep table talk appropriate for all players',
      'C) Ignore it — table banter is not the dealer\'s concern',
      'D) Ask the uncomfortable players to request a table change'
    ],
    correct: 'B',
    explanation: 'The dealer maintains a respectful environment for all players. Politely asking a player to keep conversation appropriate is within the dealer\'s role. If the behavior continues, the floor is notified.'
  },

  // ── MEDIUM (10 questions) ───────────────────────────────────────────────

  {
    id: 'pc_005', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'A dealer knows one of the players personally from outside the casino. How should this affect their behavior at the table?',
    options: [
      'A) The dealer should be extra cautious to avoid any appearance of favoritism',
      'B) The dealer may be more friendly with that player since they have an existing relationship',
      'C) The dealer should request a table change to avoid the conflict',
      'D) Personal relationships have no relevance to professional dealing'
    ],
    correct: 'A',
    explanation: 'Knowing a player personally requires heightened professional neutrality, not reduced standards. The dealer should be especially careful to treat the known player exactly as they would any other — neither more favorably nor unfavorably.'
  },

  {
    id: 'pc_006', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'At the end of a session, a player offers the dealer a private cash payment in exchange for information about other players\' tendencies. What do you do?',
    options: [
      'A) Accept the information only if it would not disadvantage the players involved',
      'B) Decline firmly and report the incident to management',
      'C) Decline but keep the incident private',
      'D) Report only if the payment amount is significant'
    ],
    correct: 'B',
    explanation: 'This is an attempt at corruption. The dealer declines immediately and reports the incident to management regardless of the amount offered. There is no threshold at which sharing player information becomes acceptable. Failure to report is itself a violation.'
  },

  {
    id: 'pc_007', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'A dealer realizes mid-hand that they made a significant shuffle error and the deck may not have been properly randomized. What do you do?',
    options: [
      'A) Complete the hand and shuffle more carefully next time',
      'B) Stop the hand, call the floor, and disclose the shuffle error',
      'C) Only disclose if a player asks about the shuffle',
      'D) Complete the hand since no one noticed'
    ],
    correct: 'B',
    explanation: 'A shuffle error that may have compromised deck randomization must be disclosed to the floor. Completing the hand without disclosure is a integrity violation regardless of whether players noticed. The floor determines how to proceed.'
  },

  {
    id: 'pc_008', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'A player is clearly intoxicated and making poor decisions. Another player is taking advantage of this. What do you do?',
    options: [
      'A) Nothing — players are responsible for their own decisions regardless of condition',
      'B) Notify the floor of the situation discreetly — both the intoxication and the perceived exploitation',
      'C) Warn the player taking advantage to stop',
      'D) Ask the intoxicated player if they want to take a break'
    ],
    correct: 'B',
    explanation: 'The dealer notifies the floor discreetly. The floor handles both the intoxicated player (who may be cut off or assisted) and the potential predatory play. The dealer does not offer game advice, make rulings, or confront the exploiting player directly.'
  },

  {
    id: 'pc_009', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'A player compliments the dealer on being "lucky for them" and asks the dealer to stay at your table. How do you respond?',
    options: [
      'A) Accept the compliment and stay if permitted',
      'B) Acknowledge the compliment professionally but follow normal dealer rotation',
      'C) Tell the player that dealers have no influence on luck',
      'D) Request from management to stay at this player\'s table'
    ],
    correct: 'B',
    explanation: 'The dealer acknowledges the comment professionally without engaging with the superstition and follows normal dealer rotation procedures. Dealers do not request special assignments based on player preference, and do not validate the idea that they influence luck.'
  },

  {
    id: 'pc_010', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'Between hands, a player asks the dealer about the tournament structure and blind schedule. What do you do?',
    options: [
      'A) Decline — dealers do not provide tournament information',
      'B) Provide the information if they know it, or direct the player to the tournament desk',
      'C) Only answer if it does not delay the start of the next hand',
      'D) Answer only during breaks — not during active play'
    ],
    correct: 'B',
    explanation: 'Providing accurate tournament structure information is a service to players and within the dealer\'s role. If the dealer knows the information (blind levels, next break, etc.) they may share it between hands. If unsure, they direct the player to the tournament desk for accurate details.'
  },

  {
    id: 'pc_011', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'A dealer notices a player consistently peeking at other players\' hole cards as they are dealt. What do you do?',
    options: [
      'A) Nothing unless a player complains',
      'B) Adjust dealing technique to protect cards better and note the behavior',
      'C) Publicly call out the player for angle shooting',
      'D) Report the player to security immediately'
    ],
    correct: 'B',
    explanation: 'The dealer adjusts their dealing technique to better protect hole cards. If the behavior continues and appears intentional, the floor is discreetly notified. Public accusations without clear evidence damage the dealer\'s credibility. Prevention through technique adjustment is the first step.'
  },

  {
    id: 'pc_012', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'After a long session, a dealer is visibly fatigued and making more mistakes. What is the professional course of action?',
    options: [
      'A) Push through — professionalism means completing the shift regardless',
      'B) Inform the supervisor that they are fatigued and request a break or table change if permitted',
      'C) Drink coffee and continue without disclosure',
      'D) Ask players at the table if they would prefer a different dealer'
    ],
    correct: 'B',
    explanation: 'A fatigued dealer makes mistakes that affect game integrity. The professional action is to inform the supervisor and request relief. Attempting to hide fatigue that is affecting performance is worse than disclosing it.'
  },

  {
    id: 'pc_013', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'A player wants to discuss a floor ruling that went against them. They are directing their frustration at the dealer. What do you say?',
    options: [
      'A) Defend the floor\'s ruling',
      'B) Sympathize with the player and suggest the ruling was unfair',
      'C) Acknowledge the player\'s frustration and direct them to speak with the floor if they wish to appeal',
      'D) Ignore the player until they calm down'
    ],
    correct: 'C',
    explanation: 'The dealer acknowledges the frustration professionally without taking sides, defending the ruling, or undermining the floor. If the player wishes to appeal, they are directed to speak with the floor or tournament director.'
  },

  {
    id: 'pc_014', track: 'professional', category: 'professional_conduct', difficulty: 'medium',
    question: 'Two dealers are discussing a hand they dealt earlier — including specific details about a player\'s hand. A player overhears. What is the issue and how should dealers handle this?',
    options: [
      'A) There is no issue — the hand is already over',
      'B) Discussing specific player hands violates player privacy; dealers should not discuss hand details outside the table',
      'C) It is fine to discuss as long as names are not used',
      'D) Discussion is only prohibited while on the clock'
    ],
    correct: 'B',
    explanation: 'Player privacy is a professional obligation. Discussing specific hands, reads, or details about players\' actions — even after the event — is unprofessional and can create trust issues. Dealers discuss general strategy in training, not specific player behavior.'
  },

  // ── HARD (6 questions) ──────────────────────────────────────────────────

  {
    id: 'pc_015', track: 'professional', category: 'professional_conduct', difficulty: 'hard',
    question: 'A dealer witnesses another dealer making what appears to be a deliberate false shuffle (retaining the top of the deck). They are not certain. What is the correct action?',
    options: [
      'A) Confront the other dealer directly',
      'B) Ignore it — they cannot be certain',
      'C) Report their observation to management privately and let management investigate',
      'D) Tell the players at the table to watch the other dealer'
    ],
    correct: 'C',
    explanation: 'A suspected false shuffle is a serious integrity concern. The dealer reports their observation privately to management. Making accusations without certainty, confronting the dealer, or involving players in the suspicion are all inappropriate and potentially harmful.'
  },

  {
    id: 'pc_016', track: 'professional', category: 'professional_conduct', difficulty: 'hard',
    question: 'A dealer is dealing a high-stakes final table being streamed live. A player asks the dealer "are you nervous?" How do you respond?',
    options: [
      'A) "A little — this is a big event"',
      'B) Smile briefly and refocus on the game without engaging in personal conversation',
      'C) "No — I\'m a professional"',
      'D) Engage briefly to humanize the broadcast'
    ],
    correct: 'B',
    explanation: 'At a broadcast final table, the dealer maintains professional focus. Personal conversations distract from the game and can influence the broadcast. A brief, professional non-answer and refocus to the game is correct. The dealer is not there to provide entertainment commentary.'
  },

  {
    id: 'pc_017', track: 'professional', category: 'professional_conduct', difficulty: 'hard',
    question: 'A player who regularly tips well is in a close pot with a player who never tips. Both hands are presented at showdown and appear nearly equal. You are uncertain of the winner. What is the correct procedure?',
    options: [
      'A) Award to the bigger tipper — they deserve the edge',
      'B) Call the floor to make the ruling on the ambiguous showdown',
      'C) Award to the more experienced player',
      'D) Split the pot to avoid conflict'
    ],
    correct: 'B',
    explanation: 'Tipping history has zero influence on rulings. An ambiguous showdown is referred to the floor. Awarding pots based on tipping behavior is corruption and grounds for immediate termination. Every ruling is made on the merits of the cards alone.'
  },

  {
    id: 'pc_018', track: 'professional', category: 'professional_conduct', difficulty: 'hard',
    question: 'A dealer consistently deals faster to players they like and slower to those they don\'t. No official complaints have been made. Is this a problem?',
    options: [
      'A) No — dealing speed variations are normal and not actionable',
      'B) Yes — inconsistent dealing that discriminates between players is a conduct violation even without complaints',
      'C) Only a problem if it affects the outcome of hands',
      'D) Only a problem if other dealers notice and report it'
    ],
    correct: 'B',
    explanation: 'Consistent, fair treatment of all players is a fundamental professional standard. Discriminatory dealing speed based on personal preference is a conduct violation regardless of whether complaints are made or outcomes are affected. Professional dealing means the same standard for every player, every hand.'
  },

  {
    id: 'pc_019', track: 'professional', category: 'professional_conduct', difficulty: 'hard',
    question: 'After a session, a player approaches a dealer privately and says "I need to tell you something about the player in seat 3 — they were cheating." What do you do?',
    options: [
      'A) Listen to the full allegation and decide whether to report it',
      'B) Immediately direct the player to management and accompany them if possible',
      'C) Take notes on the allegation and submit a written report later',
      'D) Tell the player to report it themselves — it is not the dealer\'s concern after the session'
    ],
    correct: 'B',
    explanation: 'Cheating allegations require immediate management involvement. The dealer should direct the player to management immediately and offer to accompany them. Handling the allegation independently (taking notes and filing later, or dismissing it) risks losing important evidence and violates proper protocol.'
  },

  {
    id: 'pc_020', track: 'professional', category: 'professional_conduct', difficulty: 'hard',
    question: 'A tournament director asks a dealer to deal a specific hand a certain way to "speed up the tournament." The instruction would require deviating from standard dealing procedure. What do you do?',
    options: [
      'A) Follow the TD\'s instruction — they outrank the dealer',
      'B) Comply if the deviation seems minor',
      'C) Decline and ask for the instruction in writing from the head TD',
      'D) Follow the standard procedure and professionally decline the deviation, flagging it to a supervisor'
    ],
    correct: 'D',
    explanation: 'No one may instruct a dealer to deviate from standard procedure in ways that could compromise game integrity — not even a TD. The dealer follows standard procedure and flags the unusual instruction to a supervisor. An instruction to deal improperly is a red flag regardless of who gives it.'
  },

];

export default PROFESSIONAL_CONDUCT_QUESTIONS;
